import PageHeader from "@/ui/components/PageHeader";
import { formatDiff } from "@/utils/util";
import { ArrowUpIcon } from "@heroicons/react/16/solid";
import { NewspaperIcon } from "@heroicons/react/24/outline";
import { changelogEntries, formatChangelogDate } from "@/data/changelog";
import { useEffect, useState } from "react";

function pluralize(count: number, unit: string): string {
  return count === 1 ? `${count} ${unit}` : `${count} ${unit}s`;
}

function formatAgeInWords(ageSeconds: number): string {
  const times = formatDiff(ageSeconds);
  const parts: string[] = [];

  if (times.days > 0) parts.push(pluralize(times.days, "day"));
  if (times.hours > 0) parts.push(pluralize(times.hours, "hour"));
  if (times.minutes > 0) parts.push(pluralize(times.minutes, "minute"));

  if (parts.length === 0) return "less than a minute";

  return parts.join(" ");
}

type ManualUpdateResult =
  | { status: "updated" }
  | { status: "cooldown"; retryAfterSeconds: number }
  | { status: "failed" };

function describeUpdateResult(result: ManualUpdateResult): string {
  if (result.status === "updated") return "Updated";
  if (result.status === "failed") return "Update failed";

  const retryAfterMinutes = Math.ceil(result.retryAfterSeconds / 60);
  return `Try again in ${pluralize(retryAfterMinutes, "minute")}`;
}

export default function HomePage() {
  const [dataAge, setDataAge] = useState("");
  const [updateStatusMessage, setUpdateStatusMessage] = useState("");
  const latestChangelogEntry = changelogEntries[0];

  async function loadMarketdataAge() {
    const res: any = await fetch("/api/marketdata/age");
    if (!res.ok) {
      return;
    }
    const data: any = await res.json();
    const ts: any = data.timestamp;

    const nowSec = Math.floor(Date.now() / 1000);
    setDataAge(formatAgeInWords(nowSec - ts));
  }

  async function updateData(event: React.MouseEvent) {
    event.preventDefault();
    setUpdateStatusMessage("Updating...");

    try {
      const res = await fetch("/api/marketdata/update");
      const result: ManualUpdateResult = await res.json();
      setUpdateStatusMessage(describeUpdateResult(result));
    } catch {
      setUpdateStatusMessage("Update failed");
    }

    await loadMarketdataAge();
  }

  useEffect(() => {
    loadMarketdataAge();
  }, []);

  return (
    <div className="mt-4 px-4 sm:px-6 lg:px-8">
      <PageHeader
        title="Milky Way Idle Profit Calculator"
        description="Idle Efficiency helps Milky Way Idle players find the most
        profitable items to coinify, transmute, decompose, craft and woodcut.
        Calculations are based on live marketplace prices, drop tables and
        optional buffs. Keep an eye on the market data age so your numbers stay
        current."
      />

      <div className="grid sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 mt-4 px-4 sm:px-6 lg:px-8">
        <GridItem
          title="Coinify"
          description="Market to Coinify"
          cta="Show my profits"
          link="/coinify"
          color="bg-amber-500"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
            strokeWidth="1.5"
            stroke="white"
            className="size-6"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M12 6v12m-3-2.818.879.659c1.171.879 3.07.879 4.242 0 1.172-.879 1.172-2.303 0-3.182C13.536 12.219 12.768 12 12 12c-.725 0-1.45-.22-2.003-.659-1.106-.879-1.106-2.303 0-3.182s2.9-.879 4.006 0l.415.33M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z"
            />
          </svg>
        </GridItem>

        <GridItem
          title="Transmute"
          description="Buy and Transmute"
          cta="Show my profits"
          link="/transmute"
          color="bg-amber-500"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
            strokeWidth="1.5"
            stroke="white"
            className="size-6"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M15.59 14.37a6 6 0 0 1-5.84 7.38v-4.8m5.84-2.58a14.98 14.98 0 0 0 6.16-12.12A14.98 14.98 0 0 0 9.631 8.41m5.96 5.96a14.926 14.926 0 0 1-5.841 2.58m-.119-8.54a6 6 0 0 0-7.381 5.84h4.8m2.581-5.84a14.927 14.927 0 0 0-2.58 5.84m2.699 2.7c-.103.021-.207.041-.311.06a15.09 15.09 0 0 1-2.448-2.448 14.9 14.9 0 0 1 .06-.312m-2.24 2.39a4.493 4.493 0 0 0-1.757 4.306 4.493 4.493 0 0 0 4.306-1.758M16.5 9a1.5 1.5 0 1 1-3 0 1.5 1.5 0 0 1 3 0Z"
            />
          </svg>
        </GridItem>

        <GridItem
          title="Decompose"
          description="Decompose the market"
          cta="Show my profits"
          link="/decompose"
          color="bg-amber-500"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
            strokeWidth="1.5"
            stroke="white"
            className="size-6"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M9.75 3.104v5.714a2.25 2.25 0 0 1-.659 1.591L5 14.5M9.75 3.104c-.251.023-.501.05-.75.082m.75-.082a24.301 24.301 0 0 1 4.5 0m0 0v5.714c0 .597.237 1.17.659 1.591L19.8 15.3M14.25 3.104c.251.023.501.05.75.082M19.8 15.3l-1.57.393A9.065 9.065 0 0 1 12 15a9.065 9.065 0 0 0-6.23-.693L5 14.5m14.8.8 1.402 1.402c1.232 1.232.65 3.318-1.067 3.611A48.309 48.309 0 0 1 12 21c-2.773 0-5.491-.235-8.135-.687-1.718-.293-2.3-2.379-1.067-3.61L5 14.5"
            />
          </svg>
        </GridItem>

        <GridItem
          title="Woodcutting"
          description="Profits for each woodcutting action"
          cta="Show my profits"
          link="/woodcutting"
          color="bg-lime-600"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 32 32"
            strokeWidth="1.5"
            stroke="white"
            className="size-6"
          >
            <g fill="#ffffff">
              <path d="M12.5 15a.5.5 0 0 0-.5.5v4a.5.5 0 0 0 1 0v-4a.5.5 0 0 0-.5-.5ZM16 8c2.761 0 5-.84 5-1.875c0-1.036-2.239-1.875-5-1.875s-5 .84-5 1.875C11 7.161 13.239 8 16 8Zm0-1c-1.657 0-3-.448-3-1s1.343-1 3-1s3 .448 3 1s-1.343 1-3 1Z" />
              <path d="M28.06 7.232a2.99 2.99 0 0 0-2.063-.878c-.043-1.078-.564-1.961-1.188-2.606c-.643-.665-1.476-1.173-2.35-1.555C20.702 1.425 18.418 1 15.998 1c-2.417 0-4.702.425-6.458 1.193c-.874.382-1.708.89-2.35 1.555C6.539 4.422 6 5.355 6 6.5v8.386a2.996 2.996 0 0 0-2.56.846l-.708.707a3 3 0 0 0 0 4.243L6 23.95v1.55c0 1.145.539 2.078 1.19 2.753c.643.664 1.477 1.171 2.351 1.554C11.297 30.575 13.581 31 16 31c2.08 0 4.047-.314 5.662-.88c.972-.341 1.888-.799 2.627-1.394C25.163 28.022 26 26.939 26 25.5V14.95l2.768-2.768a3 3 0 0 0 0-4.243l-.707-.707ZM8 18.88V9.945c.314.226.651.427 1 .606V14.5a.5.5 0 0 0 1 0v-3.505c1.686.65 3.787 1.005 6 1.005c2.418 0 4.703-.425 6.458-1.193c.875-.383 1.709-.89 2.351-1.555c.212-.219.412-.465.585-.737a1 1 0 0 1 1.252.131l.708.708a1 1 0 0 1 0 1.414L24 14.12V25.5c0 .604-.35 1.172-.966 1.668a5.62 5.62 0 0 1-1.034.647V23.5a.5.5 0 1 0-1 0v4.732c-1.37.48-3.108.768-5 .768c-4.418 0-8-1.567-8-3.5v-2.379l-3.854-3.853a1 1 0 0 1 0-1.415l.708-.707a1 1 0 0 1 1.414 0L8 18.88ZM24 6.5c0 1.933-3.582 3.5-8 3.5S8 8.433 8 6.5S11.582 3 16 3s8 1.567 8 3.5Z" />
            </g>
          </svg>
        </GridItem>
        <GridItem
          title="Lumber Crafting"
          description="Profits for each lumber action"
          cta="Show my profits"
          link="/crafting"
          color="bg-lime-600"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 512 512"
            strokeWidth="1.5"
            stroke="white"
            className="size-6"
          >
            <path
              fill="#ffffff"
              d="M268.4 48.99L42.55 335H82.2l75.9-104.2l14.6 10.6l-68.3 93.6h27.5l28.2-30.4l-1.6 30.4h116.8L470.9 48.99h-50.6l-150 219.91l-14.8-10.2L398.6 48.99h-34.9l-47.8 66.51l-14.6-10.6l40.2-55.91zM479 69.08L289 346.8v30.7l68.9-100.8l14.8 10.2L289 409.3v32.3l190-292.3v-11.7l-22 5.8l22-33.3zM32.98 353v110h23.83l10.54-38.1L78.48 463h37.62c-2.2-5.7-3.4-11.8-3.4-18.2c0-18.6 10.4-35 25.9-46.3c15.5-11.2 36.2-17.8 58.9-17.8c22.7 0 43.4 6.6 58.9 17.8c5.6 4.1 10.6 8.9 14.6 14.2V353zm164.52 45.7c-19.2 0-36.4 5.7-48.3 14.4c-12 8.7-18.5 19.8-18.5 31.7c0 6.4 1.9 12.5 5.5 18.2h16.3c-3.3-4.7-5.4-10.1-5.4-16.1c0-11.2 7.1-20.4 16.5-26.3c9.3-6 21.3-9.2 34.4-9.2c13.1 0 25.1 3.2 34.4 9.2c9.4 5.9 16.5 15.1 16.5 26.3c0 6-2.1 11.4-5.4 16.1h15.3c3.6-5.7 5.5-11.8 5.5-18.2c0-11.9-6.5-23-18.5-31.7c-11.9-8.7-29.1-14.4-48.3-14.4zm.5 30.7c-10 0-19 2.7-24.8 6.4c-5.8 3.7-8.1 7.6-8.1 11.1s2.3 7.4 8.1 11.1c3.3 2.1 7.6 3.9 12.5 5h24.6c4.9-1.1 9.2-2.9 12.5-5c5.8-3.7 8.1-7.6 8.1-11.1s-2.3-7.4-8.1-11.1c-5.8-3.7-14.8-6.4-24.8-6.4z"
            />
          </svg>
        </GridItem>

        <GridItem
          title={`Updated ${dataAge} ago`}
          description="Market data age"
          cta="Update market data"
          link="#"
          onClick={updateData}
          statusMessage={updateStatusMessage}
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
            strokeWidth={1.5}
            stroke="white"
            className="size-6"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M20.25 6.375c0 2.278-3.694 4.125-8.25 4.125S3.75 8.653 3.75 6.375m16.5 0c0-2.278-3.694-4.125-8.25-4.125S3.75 4.097 3.75 6.375m16.5 0v11.25c0 2.278-3.694 4.125-8.25 4.125s-8.25-1.847-8.25-4.125V6.375m16.5 0v3.75m-16.5-3.75v3.75m16.5 0v3.75C20.25 16.153 16.556 18 12 18s-8.25-1.847-8.25-4.125v-3.75m16.5 0c0 2.278-3.694 4.125-8.25 4.125s-8.25-1.847-8.25-4.125"
            />
          </svg>
        </GridItem>

        {latestChangelogEntry && (
          <GridItem
            title="Latest updates"
            description={`Changelog from ${formatChangelogDate(latestChangelogEntry.date)}`}
            cta="Read the changelog"
            link="/blog"
            color="bg-sky-600"
          >
            <NewspaperIcon className="size-6 text-white" />
          </GridItem>
        )}

        <GridItem
          title="Support & Feedback"
          description="Contact Idle Efficiency"
          cta="support@idleefficiency.com"
          link="mailto:support@idleefficiency.com"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
            strokeWidth="1.5"
            stroke="white"
            className="size-6"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M21.75 6.75v10.5a2.25 2.25 0 0 1-2.25 2.25h-15a2.25 2.25 0 0 1-2.25-2.25V6.75m19.5 0A2.25 2.25 0 0 0 19.5 4.5h-15a2.25 2.25 0 0 0-2.25 2.25m19.5 0v.243a2.25 2.25 0 0 1-1.07 1.916l-7.5 4.615a2.25 2.25 0 0 1-2.36 0L3.32 8.91a2.25 2.25 0 0 1-1.07-1.916V6.75"
            />
          </svg>
        </GridItem>

        <GridItem
          title="Milky Way Idle"
          description="Multiplayer Idle RPG"
          cta="Play the game for free"
          link="https://www.milkywayidle.com/"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
            strokeWidth="1.5"
            stroke="white"
            className="size-6"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M15.042 21.672 13.684 16.6m0 0-2.51 2.225.569-9.47 5.227 7.917-3.286-.672ZM12 2.25V4.5m5.834.166-1.591 1.591M20.25 10.5H18M7.757 14.743l-1.59 1.59M6 10.5H3.75m4.007-4.243-1.59-1.59"
            />
          </svg>
        </GridItem>
      </div>
    </div>
  );
}

function GridItem({
  title,
  description,
  cta,
  link,
  children,
  onClick,
  color,
  statusMessage,
}: {
  title: string;
  description: string;
  cta: string;
  link: string;
  children?: React.ReactNode;
  onClick?: (event: React.MouseEvent) => void;
  color?: string;
  statusMessage?: string;
}) {
  return (
    <div>
      <dl className="block w-full mt-5 grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
        <div className="relative overflow-hidden rounded-lg bg-white px-4 pt-5 pb-12 shadow-sm sm:px-6 sm:pt-6">
          <dt>
            <div
              className={`absolute rounded-md ${color ?? "bg-indigo-500"} p-3 white`}
            >
              {children}
            </div>
            <p className="ml-16 truncate text-sm font-medium text-gray-500">
              {description}
            </p>
          </dt>
          <dd className="ml-16 flex items-baseline pb-6 sm:pb-7">
            <p className="text-2xl font-semibold text-gray-900">{title}</p>
          </dd>
          <div className="absolute inset-x-0 bottom-0 bg-gray-50 px-4 py-4 sm:px-6">
            <div className="text-sm">
              <a
                onClick={onClick ?? undefined}
                href={link}
                className="font-medium text-indigo-600 hover:text-indigo-500"
              >
                <span className="">{cta}</span>
              </a>
              {statusMessage && (
                <span className="ml-3 text-gray-500">{statusMessage}</span>
              )}
            </div>
          </div>
        </div>
      </dl>
    </div>
  );
}
